import { createSelector } from 'reselect';
import { change } from 'redux-form';
import forEach from 'lodash/forEach';
import isNull from 'lodash/isNull';
import cloneDeep from 'lodash/cloneDeep';
import find from 'lodash/find';
import isEqual from 'lodash/isEqual';

// init state
const defaultInitialState = {
	traffic: {
		isOpen: false,
		childColumns: null,
		form: null,
		type: 'traffic',
		isApplied: false
	},
	conversions: {
		isOpen: false,
		childColumns: null,
		form: null,
		type: 'conversions',
		isApplied: false
	},
	coefficients: {
		isOpen: false,
		childColumns: null,
		form: null,
		type: 'coefficients',
		isApplied: false
	},
	revenue: {
		isOpen: false,
		childColumns: null,
		form: null,
		type: 'revenue',
		isApplied: false
	}
};

const createColumnControl = ({ namespace, stateNodeName = 'columnControl' }) => {
	// action types
	const TOGGLE = `${namespace}/${stateNodeName}-toggle`;
	const APPLY = `${namespace}/${stateNodeName}-apply`;
	const APPLY_BY_EXTERNAL_QUERY = `${namespace}/${stateNodeName}-apply-by-external-query`;
	const RESET = `${namespace}/${stateNodeName}-reset`;

	// init state
	const initialState = {
		...defaultInitialState
	};

	// actions
	const actions = {
		[`${stateNodeName}Toggle`]: parentColumnName => ({
			type: TOGGLE,
			payload: parentColumnName
		}),
		apply: (formData, parentColumnName, applyStatsAction) => (dispatch, getState) => {
			dispatch({
				type: APPLY,
				payload: {
					formData,
					parentColumnName
				}
			});

			applyStatsAction(dispatch, getState);
		},
		[`${stateNodeName}Reset`]: parentColumnName => ({
			type: RESET,
			payload: parentColumnName
		}),
		[`${stateNodeName}ApplyByExternalQuery`]: externalQuery => ({
			type: APPLY_BY_EXTERNAL_QUERY,
			payload: externalQuery
		}),
		[`${stateNodeName}DispatchFormAction`]: (formName, fieldName, value) => dispatch =>
			dispatch(change(formName, fieldName, value))
	};

	// reducers
	const reducers = {
		[TOGGLE]: (state, action) => {
			const parentColumnName = action.payload;
			const columnControlData = state.columnControl[parentColumnName];
			return {
				...state,
				columnControl: {
					...state.columnControl,
					[parentColumnName]: {
						...columnControlData,
						isOpen: !columnControlData.isOpen
					}
				}
			};
		},
		[APPLY]: (state, action) => {
			const { formData, parentColumnName } = action.payload;
			const columnsClone = cloneDeep(state.table.columns);

			forEach(formData, (options, columnName) => {
				columnsClone[columnName]['isShow'] = formData[columnName]['isColumnVisible'];

				if (options.conditionsType.length === 0) {
					options.conditionsValue = 0;
				}

				options.couldShowConditions = false;
			});

			return {
				...state,
				table: {
					...state.table,
					columns: columnsClone
				},
				columnControl: {
					...state.columnControl,
					[parentColumnName]: {
						...state.columnControl[parentColumnName],
						form: formData,
						isOpen: false,
						isApplied: !isEqual(formData, state.columnControl[parentColumnName].form)
					}
				}
			};
		},
		[APPLY_BY_EXTERNAL_QUERY]: (state, action) => {
			const data = action.payload;
			const columnControlClone = cloneDeep(state.columnControl);
			const columns = cloneDeep(state.table.columns);

			forEach(columnControlClone, (options, parentColumnName) => {
				const form = {};
				forEach(columns, (column, childColumnName) => {
					if (column.parent === parentColumnName) {
						const columnShouldShow = data.table
							? !(data.table.indexOf(childColumnName) !== -1)
							: columns[childColumnName]['isShow'];

						// disabled columns
						columns[childColumnName]['isShow'] = columnShouldShow;

						const conditions = find(data.conditions, { columnName: childColumnName }) || defaultChildColumnsFormState;

						// added filter initValue for form
						form[childColumnName] = {
							...conditions,
							isColumnVisible: columnShouldShow,
							couldShowConditions: false
						};
					}
				});
				options['form'] = form;
			});

			return {
				...state,
				table: {
					...state.table,
					columns: {
						...columns
					}
				},
				columnControl: {
					...columnControlClone
				}
			};
		},

		[RESET]: (state, action) => {
			const parentColumnName = action.payload;
			const { columns } = state.table;
			const form = getDefaultFormState(columns, parentColumnName);

			return {
				...state,
				columnControl: {
					...state.columnControl,
					[parentColumnName]: {
						...state.columnControl[parentColumnName],
						form: form
					}
				}
			};
		}
	};

	// selectors
	const selectors = {
		getConfig: (columnControlSelector, appState) =>
			createSelector(columnControlSelector, appState, (columnControl, appState) => {
				const { columns } = appState.statistics.table;
				const res = {};

				forEach(columnControl, (parentColumn, parentKey) => {
					res[parentKey] = {
						...parentColumn,
						childColumns: isNull(parentColumn.childColumns)
							? getChildColumns(columns, parentKey)
							: parentColumn.childColumns,
						form: isNull(parentColumn.form) ? getDefaultFormState(columns, parentKey) : parentColumn.form
					};
				});
				return res;
			})
	};

	return {
		initialState,
		actions,
		reducers,
		selectors
	};
};

export default createColumnControl;

const getChildColumns = (columns, parentKey) => {
	const res = {};

	forEach(columns, (column, key) => {
		if (column.parent === parentKey) {
			res[key] = column;
		}
	});

	return res;
};

const getDefaultFormState = (columns, parentKey) => {
	const res = {};

	forEach(columns, (column, key) => {
		if (column.parent === parentKey) {
			res[key] = defaultChildColumnsFormState;
		}
	});

	return res;
};

const defaultChildColumnsFormState = {
	isColumnVisible: true,
	couldShowConditions: false,
	conditionsValue: 0,
	conditionsType: ''
};



// WEBPACK FOOTER //
// ./frontend/cabinets/modules/statistics/components/column-control/index.js